import { Parameters } from '@app/models/Parameters';
import { post, get } from './http.api';
import {
  FollowUpRequest,
  FollowUpResponse,
  FollowUpTableData,
  FollowUpTableRow,
} from '@app/models/follow_up/FollowUpResponse';
import { PCComment } from '@app/models/follow_up/FollowUpRequest';

export const getFollowUpTableData = async (parameters: Parameters): Promise<FollowUpTableData> => {
  try {
    const { data, status } = await get<any>('/followups', { params: parameters }, true);
    console.log(`Status: ${status}`, `Data:`, data);

    const followUpsResponse = data?.follow_ups;

    const followUps: FollowUpTableRow[] =
      followUpsResponse?.data?.map((followUp: any) => ({
        id: followUp.id,
        code: followUp.code,
        status: followUp.status,
        level: followUp.level,
        import_id: followUp.import_id,
        expire_date: followUp.expire_date,
      })) || [];

    return {
      status_options: data.status_options || [], // optional (if exists later)
      data: followUps,
      pagination: {
        current: followUpsResponse?.current_page || 1,
        pageSize: followUpsResponse?.per_page || 5,
        total: followUpsResponse?.total || 0,
      },
    };
  } catch (error: any) {
    console.error('Error fetching FollowUps:', error);
    throw error;
  }
};

export const createFollowUp = async (): Promise<FollowUpResponse> => {
  try {
    const { data, status } = await get<FollowUpResponse>(
      '/import/${payload.import_id}/followups/create',
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data:`, data);
    return data;
  } catch (error: any) {
    console.error('Error creating FollowUp:', error);
    throw error;
  }
};

export const createInitialFollowUp = async (
  importId: number,
  payload: {
    arrival_line_id: number;
    import_line_variety_ids: number[];
  },
) => {
  try {
    const response = await post<any>(`/import/${importId}/followups/create`, payload);

    console.log(`Status: ${response.status}`, `Data:`, response.data);

    return response.data;
  } catch (error: any) {
    console.error('Error updating FollowUp:', error);
    throw error;
  }
};

export const createNextFollowUp = async (parent_id: number) => {
  try {
    const response = await post<any>(`/followups/${parent_id}/create`, undefined);
    console.log(`Status: ${response.status}`, `Data:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating FollowUp:', error);
    throw error;
  }
};

export const storeFollowUp = async (payload: {
  import_id: number | string;
  status: string;
  customer_id: number | null;
  level: number;
  department_id: number | null;
  expiry_date: string;
}): Promise<FollowUpResponse> => {
  try {
    const url = `/import/${payload.import_id}/followups/create`;
    console.log('storeFollowUp URL:', url);
    console.log('Payload:', payload);
    const response = await post<FollowUpResponse>(url, payload);
    return response.data;
  } catch (error: any) {
    console.error('Error storing FollowUp:', error);
    throw error;
  }
};

export const showFollowUp = async (follow_up_id: number) => {
  try {
    const { data, status } = await get<FollowUpResponse>(`/followups/${follow_up_id}`, undefined, true);
    console.log(`Status: ${status}`, `Data:`, data);
    return data;
  } catch (error: any) {
    console.error('Error showing FollowUp:', error);
    throw error;
  }
};

export const editFollowUp = async (id: number) => {
  try {
    const { data, status } = await get<FollowUpResponse>(`/followups/${id}/edit`, undefined, true);
    console.log(`Status: ${status}`, `Data:`, data);
    return data;
  } catch (error: any) {
    console.error('Error editing FollowUp:', error);
    throw error;
  }
};

export const updateFollowUp = async (id: number, followUpData: FollowUpRequest) => {
  try {
    const response = await post<any>(`/followups/${id}/edit`, followUpData, undefined);
    console.log(`Status: ${response.status}`, `Data:`, response.data);
    return response.data;
  } catch (error: any) {
    console.error('Error updating FollowUp:', error);
    throw error;
  }
};

export const storeComment = async (id: number, commentData: PCComment) => {
  try {
    const response = await post<any>(`/followups/${id}/store_comment`, commentData, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const updateComment = async (id: number, com_id: number, commentData: PCComment) => {
  try {
    const { data, status } = await post<PCComment>(
      `/followups/${id}/${com_id}/update_comment`,
      commentData,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export interface UploadAdditionalDocumentResponse {
  data: {
    import_additional_documents: {
      name: string;
      file: string;
      document_id: string;
    }[];
  };
  message: string;
}

export const uploadAdditionalDocument = async (
  follow_up_id: number,
  formData: FormData,
): Promise<UploadAdditionalDocumentResponse | null> => {
  try {
    const { data, status } = await post<UploadAdditionalDocumentResponse>(
      `followups/${follow_up_id}/upload_additional`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    console.log('Status:', status, 'Data:', data);
    return data;
  } catch (error: any) {
    console.error('Upload error:', error);

    if (error.response) {
      const { status, data } = error.response;
      console.error(`Error ${status}:`, data.errors || data.message);
    } else {
      alert('A network error occurred. Please check your connection.');
    }

    return null;
  }
};

export const updateStatus = async (follow_up_id: number, action: any) => {
  try {
    const { data, status } = await post<FollowUpRequest>(
      `followups/${follow_up_id}/do_action`,
      action,
      undefined,
      true,
    );
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const generateFollowUpInvoice = async (id: number, invoiceType: string) => {
  try {
    const response = await post<any>(`followups/${id}/create_invoice`, { invoice_type: invoiceType }, undefined, true);
    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};

export const getInvoice = async (id: number) => {
  try {
    const { data, status } = await get<any>(`/invoices/${id}`, undefined, true);
    console.log(`Status: ${status}`, `Data: ${data}`);
    return data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw new Error(`Error: ${error.status} - ${error.message}`);
  }
};

export const handleFollowUpFailure = async (
  id: number,
  payload: {
    action_after_failure: string;
    msg?: string | null;
    comment_for?: string | null;
  },
) => {
  try {
    const response = await post<any>(`followups/${id}/do_fail`, payload, undefined, true);

    console.log(`Status: ${response.status}`, `Data: ${JSON.stringify(response.data)}`);

    return response.data;
  } catch (error: any) {
    console.error(`Error: ${error.status} - ${error.message}`);
    throw error;
  }
};
